/* eslint-disable no-undef */
import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  Box,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import RefreshIcon from '@mui/icons-material/Refresh';
import verificareToken from './VerificareToken';

const SolutionareRezultatDosare = ({ open, onClose, dosareData }) => {
  const { token } = verificareToken();
  const [progress, setProgress] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [completed, setCompleted] = useState(0);
  const [total, setTotal] = useState(0);
  const [errors, setErrors] = useState([]);
  const [success, setSuccess] = useState(false);
  const [results, setResults] = useState([]);
  const [retryingId, setRetryingId] = useState(null);

  const chunkArray = (array, size) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  };

  // Funcție pentru formatarea mesajelor de eroare
  const formatErrorMessage = (error, statusCode) => {
    // Erori HTTP comune
    if (statusCode === 429) {
      return 'Prea multe cereri. Vă rugăm așteptați și reîncercați.';
    }
    if (statusCode === 500) {
      return 'Eroare server. Vă rugăm reîncercați mai târziu.';
    }
    if (statusCode === 503) {
      return 'Serviciu temporar indisponibil. Reîncercați în câteva momente.';
    }
    if (statusCode === 404) {
      return 'Resursa nu a fost găsită.';
    }
    if (statusCode === 401 || statusCode === 403) {
      return 'Acces neautorizat. Verificați permisiunile.';
    }

    // Erori de parsing JSON (inclusiv "Unexpected token")
    if (error && (
      error.includes('JSON.parse') || 
      error.includes('Unexpected token') || 
      error.includes('is not valid JSON') ||
      error.includes('JSON')
    )) {
      return 'A apărut o problemă tehnică. Vă rugăm reîncercați.';
    }

    // Erori de rețea
    if (error && (error.includes('Failed to fetch') || error.includes('NetworkError'))) {
      return 'Eroare de conexiune. Verificați conexiunea la internet.';
    }

    if (error && error.includes('timeout')) {
      return 'Cererea a expirat. Reîncercați.';
    }

    // Mesaj generic elegant pentru orice altă eroare tehnică
    if (error && error.length > 100) {
      return 'A apărut o problemă. Vă rugăm reîncercați.';
    }

    // Returnează mesajul original doar dacă e scurt și clar
    return error || 'A apărut o problemă. Vă rugăm reîncercați.';
  };

  const processDosare = useCallback(async () => {
    setProcessing(true);
    setProgress(0);
    setCompleted(0);
    setErrors([]);
    setSuccess(false);
    setResults([]);

    const dosareIds = dosareData.map(row => row.id).filter(id => id);
    setTotal(dosareIds.length);

    if (dosareIds.length === 0) {
      setErrors(['Nu există dosare selectate pentru procesare.']);
      setProcessing(false);
      return;
    }

    const chunks = chunkArray(dosareIds, 10);
    let processedCount = 0;
    const errorList = [];
    const resultList = [];

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      
      const promises = chunk.map(async (id) => {
        try {
          const response = await fetch(`${window.API_URL}rezultatDosar/update/${id}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({})
          });

          const responseData = await response.json();

          if (response.ok) {
            const d = responseData.data || {};
            const result = { 
              success: true, 
              id, 
              numarDosar: d.detalii?.numar_dosar || '-',
              rezultat: d.rezultat || '-',
              motiv: d.motiv || '-',
              calitate: d.detalii?.calitate || '-',
              finalizat: d.detalii?.finalizat
            };
            
            // Actualizare progres imediat după fiecare răspuns
            processedCount++;
            resultList.push(result);
            setCompleted(processedCount);
            setResults([...resultList]);
            setProgress(((processedCount + errorList.length) / dosareIds.length) * 100);
            
            return result;
          } else {
            const errorResult = {
              success: false,
              id,
              numarDosar: dosareData.find(d => d.id === id)?.numardosarfinal || `ID: ${id}`,
              error: formatErrorMessage(responseData.message, response.status),
              statusCode: response.status
            };
            errorList.push(errorResult);
            
            // Actualizare progres și pentru erori
            setErrors([...errorList]);
            setProgress(((processedCount + errorList.length) / dosareIds.length) * 100);
            
            return errorResult;
          }
        } catch (error) {
          const errorResult = {
            success: false,
            id,
            numarDosar: dosareData.find(d => d.id === id)?.numardosarfinal || `ID: ${id}`,
            error: formatErrorMessage(error.message, null),
            statusCode: null
          };
          errorList.push(errorResult);
          
          // Actualizare progres și pentru erori
          setErrors([...errorList]);
          setProgress(((processedCount + errorList.length) / dosareIds.length) * 100);
          
          return errorResult;
        }
      });

      await Promise.all(promises);
      
      // Pauză de 1 secundă între chunk-uri (nu se aplică la ultimul chunk)
      if (i < chunks.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    setProcessing(false);
    setSuccess(true);
  }, [dosareData, token]);

  const handleRetry = async (errorItem) => {
    setRetryingId(errorItem.id);
    
    try {
      const response = await fetch(`${window.API_URL}rezultatDosar/update/${errorItem.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({})
      });

      const responseData = await response.json();

      if (response.ok) {
        const d = responseData.data || {};
        const newResult = { 
          success: true, 
          id: errorItem.id, 
          numarDosar: d.detalii?.numar_dosar || errorItem.numarDosar,
          rezultat: d.rezultat || '-',
          motiv: d.motiv || '-',
          calitate: d.detalii?.calitate || '-',
          finalizat: d.detalii?.finalizat
        };
        
        // Elimină din erori și adaugă în rezultate
        setErrors(prev => prev.filter(e => e.id !== errorItem.id));
        setResults(prev => [...prev, newResult]);
        setCompleted(prev => prev + 1);
      } else {
        // Actualizează eroarea cu noul mesaj
        setErrors(prev => prev.map(e => 
          e.id === errorItem.id 
            ? { ...e, error: formatErrorMessage(responseData.message, response.status), statusCode: response.status }
            : e
        ));
      }
    } catch (error) {
      // Actualizează eroarea cu noul mesaj
      setErrors(prev => prev.map(e => 
        e.id === errorItem.id 
          ? { ...e, error: formatErrorMessage(error.message, null), statusCode: null }
          : e
      ));
    } finally {
      setRetryingId(null);
    }
  };

  const handleClose = () => {
    if (!processing) {
      setResults([]);
      setProgress(0);
      setCompleted(0);
      setErrors([]);
      setSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        handleClose();
      }} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          height: 'auto'
        }
      }}
    >
      <DialogTitle className='titluModal'>
        <AssignmentTurnedInIcon style={{ marginRight: '8px' }} /> Soluționare favorabilă / nefavorabilă
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={processing}
          sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className='mt-3'>
        {!processing && !success && errors.length === 0 && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Ce face această acțiune?
              </Typography>
              <Typography variant="body2">
                Stabilește caracterul favorabil sau nefavorabil al soluției în funcție de 
                rezultatul pronunțat și de calitatea entității reprezentate în dosar.
              </Typography>
            </Alert>
            <Typography>
              Sunteți sigur că doriți să procesați {dosareData.length} dosare?
            </Typography>
          </>
        )}

        {(processing || success) && (
          <Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {processing ? 'Se procesează dosarele...' : 'Procesare completă!'} {completed} din {total} procesate
                {errors.length > 0 && `, ${errors.length} erori`}
              </Typography>
              <Box sx={{ position: 'relative' }}>
                <LinearProgress 
                  variant="determinate" 
                  value={progress} 
                  sx={{ 
                    height: 20, 
                    borderRadius: 5,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: progress === 100 ? '#4caf50' : '#1976d2',
                      transition: 'background-color 0.3s ease'
                    }
                  }} 
                />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontWeight: 'bold',
                    fontSize: '0.875rem',
                    color: progress > 50 ? '#fff' : '#000',
                    textShadow: progress > 50 ? '0 0 2px rgba(0,0,0,0.5)' : 'none'
                  }}
                >
                  {Math.round(progress)}%
                </Typography>
              </Box>
            </Box>

            {results.length > 0 && (
              <TableContainer component={Paper} sx={{ maxHeight: 350 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Nr. dosar</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Rezultat</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Motiv</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Calitate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.numarDosar}</TableCell>
                        <TableCell>
                          <Chip 
                            label={row.rezultat} 
                            color={row.rezultat === 'favorabil' ? 'success' : row.rezultat === 'nefavorabil' ? 'error' : 'default'} 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '12px', maxWidth: 250, whiteSpace: 'normal' }}>{row.motiv}</TableCell>
                        <TableCell sx={{ fontSize: '12px' }}>{row.calitate}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {errors.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="error" sx={{ mb: 1 }}>
                  Au apărut {errors.length} erori:
                </Alert>
                <TableContainer component={Paper} sx={{ maxHeight: 200 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Nr. dosar</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Eroare</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: 100 }}>Acțiune</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {errors.map((error, index) => (
                        <TableRow key={index}>
                          <TableCell>{error.numarDosar}</TableCell>
                          <TableCell sx={{ fontSize: '12px' }}>
                            {error.statusCode && (
                              <Chip 
                                label={`${error.statusCode}`} 
                                size="small" 
                                color="error" 
                                sx={{ mr: 1, fontSize: '10px' }} 
                              />
                            )}
                            {error.error}
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Reîncearcă">
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={() => handleRetry(error)}
                                disabled={retryingId === error.id}
                              >
                                <RefreshIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={handleClose} disabled={processing}>
          {success ? 'Închide' : 'Renunțare'}
        </Button>
        {!processing && !success && (
          <Button size="small" onClick={processDosare} variant="contained" color="primary">
            Confirmă Procesare
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SolutionareRezultatDosare;
